import type { RimBoardAlignment } from "building-types/floors/floorComponentsTypes";
import { FloorFramingComponentType } from "building-types/framing/floorFramingComponentTypes";
import type {
  GeneratedJoist,
  JoistPlanarGraphResult,
  MemberConnectionPoint,
} from "building-types/framing/floorFramingTypes";
import { DIST_EPSILON } from "geom/core";
import { planeByNormal, planeNew, planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { oppositeHalfEdgeIndex } from "geom/polyclip/dcelFromPolygons";
import { polyLine2Length } from "geom/polyLine2";
import type { ClosedPolyLine2 } from "geom/polyLine2BrandedTypes";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Cross, vec2Dot } from "geom/vec2";

/** Minimum |sin| of the corner angle used when projecting connected-member thickness onto this member. */
const MIN_CORNER_SIN = 0.1;

export function getMemberDirection(joist: GeneratedJoist): Vec2 {
  const dx = joist.polyline[2] - joist.polyline[0];
  const dy = joist.polyline[3] - joist.polyline[1];
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < DIST_EPSILON) {
    return [1, 0];
  }
  return [dx / length, dy / length];
}

/**
 * A header's own polyline stops at the rough-opening boundary, not at the face of the
 * (possibly angled, possibly stacked) trimmer it actually bears on - see the header/trimmer
 * offset math in `generatesOpeningsAndHeaders`. For graph connectivity (and only for graph
 * connectivity - the rendered polyline is untouched), find where a ray cast from `x,y` in
 * direction `dirX,dirY` first meets a trimmer's real, bounded segment. Returns null if no
 * trimmer lies ahead on that ray (e.g. the header and trimmer are parallel).
 */
export function findTrimmerGraphConnectionPoint(
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  trimmers: GeneratedJoist[],
): Vec2 | null {
  let closestX = 0;
  let closestY = 0;
  let closestOffset = Infinity;
  const epsilonSquared = DIST_EPSILON * DIST_EPSILON;

  for (const trimmer of trimmers) {
    const [tx1, ty1, tx2, ty2] = trimmer.polyline;
    const segmentX = tx2 - tx1;
    const segmentY = ty2 - ty1;
    const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
    if (segmentLengthSquared < epsilonSquared) {
      continue;
    }

    const originX = x - tx1;
    const originY = y - ty1;
    const originCross = segmentX * originY - segmentY * originX;
    if (originCross * originCross <= epsilonSquared * segmentLengthSquared) {
      const originDot = originX * segmentX + originY * segmentY;
      const endpointTolerance = DIST_EPSILON * Math.sqrt(segmentLengthSquared);
      if (originDot >= -endpointTolerance && originDot <= segmentLengthSquared + endpointTolerance) {
        return null;
      }
    }

    const denominator = dirX * segmentY - dirY * segmentX;
    if (denominator * denominator <= epsilonSquared * segmentLengthSquared) {
      continue;
    }

    const trimmerOffsetX = tx1 - x;
    const trimmerOffsetY = ty1 - y;
    const rayOffset = (trimmerOffsetX * segmentY - trimmerOffsetY * segmentX) / denominator;
    if (rayOffset <= DIST_EPSILON || rayOffset >= closestOffset) {
      continue;
    }

    const segmentOffset = (trimmerOffsetX * dirY - trimmerOffsetY * dirX) / denominator;
    const segmentTolerance = DIST_EPSILON / Math.sqrt(segmentLengthSquared);
    if (segmentOffset < -segmentTolerance || segmentOffset > 1 + segmentTolerance) {
      continue;
    }

    closestOffset = rayOffset;
    closestX = x + rayOffset * dirX;
    closestY = y + rayOffset * dirY;
  }

  return closestOffset < Infinity ? [closestX, closestY] : null;
}

export function computeTributaryPolygons(
  graphResult: JoistPlanarGraphResult,
  segmentIndex: number,
  memberDirection: Vec2,
): { left: ClosedPolyLine2[]; right: ClosedPolyLine2[] } {
  const { dcel } = graphResult;
  const left: ClosedPolyLine2[] = [];
  const right: ClosedPolyLine2[] = [];

  const halfEdgeToSegment = dcel.getHalfEdgeToSegment();

  const facesForMember = new Map<number, number>();
  for (let faceIdx = 0; faceIdx < dcel.faceHalfEdges.length; faceIdx++) {
    if (dcel.faceSignedAreas[faceIdx] <= 0) {
      continue;
    }
    const faceHalfEdgesList = dcel.faceHalfEdges[faceIdx];
    if (!faceHalfEdgesList) {
      continue;
    }
    for (const heIdx of faceHalfEdgesList) {
      let segIdx = halfEdgeToSegment[heIdx];
      if (segIdx < 0) {
        segIdx = halfEdgeToSegment[oppositeHalfEdgeIndex(heIdx)] ?? -1;
      }
      if (segIdx >= 0 && segIdx === segmentIndex) {
        facesForMember.set(faceIdx, heIdx);
        break;
      }
    }
  }

  for (const [faceIdx, boundaryHeIdx] of facesForMember) {
    const halfEdge = dcel.halfEdges[boundaryHeIdx];
    if (!halfEdge) {
      continue;
    }

    const startVertex = dcel.vertices[halfEdge[0]];
    const endVertex = dcel.vertices[halfEdge[1]];
    if (!startVertex || !endVertex) {
      continue;
    }

    const heDirX = endVertex[0] - startVertex[0];
    const heDirY = endVertex[1] - startVertex[1];

    const dotProduct = memberDirection[0] * heDirX + memberDirection[1] * heDirY;
    const faceIsOnLeftOfMember = dotProduct > 0;

    const faceHalfEdges = dcel.faceHalfEdges[faceIdx];
    if (!faceHalfEdges || faceHalfEdges.length < 3) {
      continue;
    }

    const polygon = dcel.buildPolygonFromFace(faceIdx);
    if (polygon.length === 0 || polygon[0].length < 3) {
      continue;
    }

    const polylineData: number[] = [];
    for (const point of polygon[0]) {
      polylineData.push(point[0], point[1]);
    }
    polylineData.push(polygon[0][0][0], polygon[0][0][1]);

    const polyline = polyLine2New(polylineData);

    let closedPolyline: ClosedPolyLine2;
    try {
      closedPolyline = closedPolyLine2New(polyline);
    } catch {
      console.error("Failed to compute tributary polygons, falling back to unmerged data");
      continue;
    }

    if (faceIsOnLeftOfMember) {
      left.push(closedPolyline);
    } else {
      right.push(closedPolyline);
    }
  }

  return { left, right };
}

export function computeMemberClipPlanesWithPositions(
  memberId: string,
  memberPolyline: PolyLine2,
  connectionPoints: MemberConnectionPoint[],
  joists: GeneratedJoist[],
  graphResult: JoistPlanarGraphResult,
  framingDirection: Vec2,
  rimBoardAlignment: RimBoardAlignment,
  rimStackDepthByMemberId?: Map<string, number>,
): [Plane | null, Plane | null] {
  const { memberIdToSegmentIndex, joistTypeByMemberId } = graphResult;
  const clipPlanes: [Plane | null, Plane | null] = [null, null];

  const memberSegIdx = memberIdToSegmentIndex.get(memberId);
  if (memberSegIdx === undefined) {
    return clipPlanes;
  }

  const memberType = joistTypeByMemberId.get(memberId);
  const memberStart: Vec2 = [memberPolyline[0], memberPolyline[1]];
  const memberEnd: Vec2 = [memberPolyline[2], memberPolyline[3]];
  const memberLength = polyLine2Length(memberPolyline);
  const memberDir = getMemberDirection(joists[memberSegIdx]);
  const isRimJoist = memberType === FloorFramingComponentType.RIM_JOIST;

  if (memberLength < DIST_EPSILON) {
    return clipPlanes;
  }

  if (isRimJoist) {
    const memberJoist = joists[memberSegIdx];
    const startRimConnection = findRimJoistConnectionAtEndpoint(
      connectionPoints,
      memberJoist?.rimStartNeighborMemberId,
    );
    if (startRimConnection) {
      clipPlanes[0] = computeRimJoistEndpointClipPlane(
        memberStart,
        memberDir,
        memberLength,
        startRimConnection,
        joists,
        graphResult,
        framingDirection,
        rimBoardAlignment,
        true,
        rimStackDepthByMemberId,
      );
    }

    const endRimConnection = findRimJoistConnectionAtEndpoint(connectionPoints, memberJoist?.rimEndNeighborMemberId);
    if (endRimConnection) {
      clipPlanes[1] = computeRimJoistEndpointClipPlane(
        memberEnd,
        memberDir,
        memberLength,
        endRimConnection,
        joists,
        graphResult,
        framingDirection,
        rimBoardAlignment,
        false,
        rimStackDepthByMemberId,
      );
    }

    return clipPlanes;
  }

  if (connectionPoints?.[0]) {
    const startClipPlane = computeEndpointClipPlane(
      memberStart,
      memberDir,
      connectionPoints[0],
      joists,
      graphResult,
      true,
    );
    if (startClipPlane) {
      clipPlanes[0] = startClipPlane;
    }
  }

  if (connectionPoints?.[connectionPoints.length - 1]) {
    const endClipPlane = computeEndpointClipPlane(
      memberEnd,
      memberDir,
      connectionPoints[connectionPoints.length - 1],
      joists,
      graphResult,
      false,
    );
    if (endClipPlane) {
      clipPlanes[1] = endClipPlane;
    }
  }

  return clipPlanes;
}

/**
 * Find the rim-joist connection that belongs to this endpoint.
 */
export function findRimJoistConnectionAtEndpoint(
  connectionPoints: MemberConnectionPoint[],
  expectedConnectedMemberId: string | undefined,
): MemberConnectionPoint | undefined {
  if (expectedConnectedMemberId === undefined) {
    return undefined;
  }

  return connectionPoints.find(
    (connection) =>
      connection.connectedMemberType === FloorFramingComponentType.RIM_JOIST &&
      connection.connectedMemberId === expectedConnectedMemberId,
  );
}

/**
 * True when the CCW framing-loop turn at this endpoint is a left turn (convex polygon corner).
 * Right turns are reflex / reentrant ("interior") corners.
 */
export function isConvexFramingCorner(memberDir: Vec2, connectedDir: Vec2, isStart: boolean): boolean {
  const cross = isStart
    ? vec2Cross(connectedDir[0], connectedDir[1], memberDir[0], memberDir[1])
    : vec2Cross(memberDir[0], memberDir[1], connectedDir[0], connectedDir[1]);
  return cross > DIST_EPSILON;
}

/**
 * Origin shift from the rail-rail connection along +memberDir.
 *
 * Convex corners: primary runs through past the connection by the full connected thickness so its
 * end reaches the secondary's outer face; secondary butts at the connection.
 *
 * Reflex corners: both boards extrude into the same exterior wedge, so primary stays at the
 * connection (its outward body fills the wedge) and secondary insets by connected thickness to
 * butt against the primary's outer face — otherwise the solids overlap.
 *
 * Alignment only affects where rails sit (via generation); the through/butt distances are always
 * the full board thickness measured from the (already alignment-adjusted) connection.
 */
export function rimClipOriginOffsetFromConnection(
  _alignment: RimBoardAlignment,
  connectedIsPrimary: boolean,
  isConvexCorner: boolean,
  thicknessAlongMember: number,
  isStart: boolean,
): number {
  // Always use the full connected thickness (angle-adjusted). CENTER rails already sit at the
  // half-inset connection; through-run / outer-face butt must still span the full board width to
  // reach the secondary's outer face — not half of it.
  const amount = thicknessAlongMember;
  const intoMemberSign = isStart ? 1 : -1;
  const pastConnectionSign = isStart ? -1 : 1;

  if (connectedIsPrimary) {
    if (isConvexCorner) {
      return 0;
    }
    return intoMemberSign * amount;
  }

  if (isConvexCorner) {
    return pastConnectionSign * amount;
  }
  return 0;
}

/**
 * Rim-rim endpoint clip plane.
 *
 * Plane origin is the DCEL rim-rim connection (rail intersection), then shifted by
 * {@link rimClipOriginOffsetFromConnection}. U follows the connected rim; V is +Z. The normal is
 * forced outward so reflex corners still keep the member body.
 */
export function computeRimJoistEndpointClipPlane(
  endpointPosition: Vec2,
  memberDir: Vec2,
  memberLength: number,
  connection: MemberConnectionPoint,
  joists: GeneratedJoist[],
  graphResult: JoistPlanarGraphResult,
  framingDirection: Vec2,
  rimBoardAlignment: RimBoardAlignment,
  isStart: boolean,
  rimStackDepthByMemberId?: Map<string, number>,
): Plane | null {
  const connectedSegIdx = graphResult.memberIdToSegmentIndex.get(connection.connectedMemberId);
  if (connectedSegIdx === undefined) {
    return null;
  }

  const connectedJoist = joists[connectedSegIdx];
  if (!connectedJoist) {
    return null;
  }

  // Rim polylines are generated from the CCW framing-loop perimeter, so both directions
  // already follow loop orientation.
  const connectedDir = getMemberDirection(connectedJoist);

  const absDotConnected = Math.abs(vec2Dot(connectedDir[0], connectedDir[1], framingDirection[0], framingDirection[1]));
  const absDotMember = Math.abs(vec2Dot(memberDir[0], memberDir[1], framingDirection[0], framingDirection[1]));
  const connectedIsPrimary = absDotConnected >= absDotMember;

  const sinCorner = Math.abs(vec2Cross(memberDir[0], memberDir[1], connectedDir[0], connectedDir[1]));
  if (sinCorner < DIST_EPSILON) {
    return null;
  }

  // Use the full stacked rim depth on the connected edge when available so all plies on this
  // edge share the same end cut relative to the adjacent stack as a whole.
  const connectedThickness =
    rimStackDepthByMemberId?.get(connection.connectedMemberId) ?? connectedJoist.profileDescriptor.height;
  const thicknessAlongMember = connectedThickness / Math.max(sinCorner, MIN_CORNER_SIN);
  const convexCorner = isConvexFramingCorner(memberDir, connectedDir, isStart);

  // Connection lies along the member at distanceFromStart; convert to offset from this endpoint.
  const alongFromEndpoint = isStart ? connection.distanceFromStart : connection.distanceFromStart - memberLength;
  const fromConnection = rimClipOriginOffsetFromConnection(
    rimBoardAlignment,
    connectedIsPrimary,
    convexCorner,
    thicknessAlongMember,
    isStart,
  );
  const originOffset = alongFromEndpoint + fromConnection;

  const originX = endpointPosition[0] + memberDir[0] * originOffset;
  const originY = endpointPosition[1] + memberDir[1] * originOffset;

  // U = connected rim direction (loop-oriented), V = +Z → vertical plane parallel to connected rim.
  let plane: Plane = planeNew(originX, originY, 0, connectedDir[0], connectedDir[1], 0, 0, 0, 1);

  const outwardX = isStart ? -memberDir[0] : memberDir[0];
  const outwardY = isStart ? -memberDir[1] : memberDir[1];
  const [nx, ny] = planeNormal(plane);
  if (nx * outwardX + ny * outwardY < 0) {
    // Flip U so U × V faces outward (required at reflex/interior corners on a CCW loop).
    plane = planeNew(originX, originY, 0, -connectedDir[0], -connectedDir[1], 0, 0, 0, 1);
  }

  return plane;
}

/**
 * Total rim stack depth per member id (sum of ply heights on the same perimeter edge).
 * Non-rim members are omitted.
 */
export function buildRimStackDepthByMemberId(joists: GeneratedJoist[]): Map<string, number> {
  const depthByEdge = new Map<number, number>();
  for (const joist of joists) {
    if (joist.rimEdgeIndex === undefined || joist.joistType !== FloorFramingComponentType.RIM_JOIST) {
      continue;
    }
    depthByEdge.set(joist.rimEdgeIndex, (depthByEdge.get(joist.rimEdgeIndex) ?? 0) + joist.profileDescriptor.height);
  }

  const depthByMemberId = new Map<string, number>();
  for (const joist of joists) {
    if (joist.rimEdgeIndex === undefined) {
      continue;
    }
    depthByMemberId.set(joist.memberId, depthByEdge.get(joist.rimEdgeIndex) ?? joist.profileDescriptor.height);
  }
  return depthByMemberId;
}

/**
 * Map each rim edge index to the joist-face (stack 0) member id on that edge.
 */
export function buildRimStack0MemberIdByEdge(joists: GeneratedJoist[]): Map<number, string> {
  const stack0ByEdge = new Map<number, string>();
  for (const joist of joists) {
    if (
      joist.rimEdgeIndex === undefined ||
      joist.rimStackIndex === undefined ||
      joist.joistType !== FloorFramingComponentType.RIM_JOIST
    ) {
      continue;
    }
    if (joist.rimStackIndex === 0) {
      stack0ByEdge.set(joist.rimEdgeIndex, joist.memberId);
    }
  }
  return stack0ByEdge;
}

function computeEndpointClipPlane(
  endpointPosition: Vec2,
  memberDir: Vec2,
  connection: MemberConnectionPoint,
  joists: GeneratedJoist[],
  graphResult: JoistPlanarGraphResult,
  isStart: boolean,
): Plane | null {
  const connectedSegIdx = graphResult.memberIdToSegmentIndex.get(connection.connectedMemberId);
  if (connectedSegIdx === undefined) {
    return null;
  }

  const connectedJoist = joists[connectedSegIdx];
  if (!connectedJoist) {
    return null;
  }

  const connectedDir = getMemberDirection(connectedJoist);

  const outwardX = isStart ? -memberDir[0] : memberDir[0];
  const outwardY = isStart ? -memberDir[1] : memberDir[1];

  // Normal ⊥ connectedDir in XY → plane is vertical and parallel to the connected member.
  let nx = -connectedDir[1];
  let ny = connectedDir[0];
  if (nx * outwardX + ny * outwardY < 0) {
    nx = -nx;
    ny = -ny;
  }

  return planeByNormal(endpointPosition[0], endpointPosition[1], 0, nx, ny, 0);
}
